import { Text } from '@avanta/shared/components/Text';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { useFetchSpecialities } from '@avanta/shared/hooks/useFetchSpecialities';
import { useGetSpecialityOptions } from '@avanta/shared/hooks/useGetSpecialityOptions';
import { formatTime, formatUnifiedDateWithMonth } from '@avanta/shared/utils/date';
import { Building, Calendar, ClipboardPen, Clock, MapPin, Stethoscope } from 'lucide-react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  type AppointmentsReserveFormData,
  AppointmentsReservePayload,
} from 'pages/AppointmentsReserve/types';
import { mapSpecialities, mapTreatments } from 'pages/AppointmentsReserve/utils';
import { useFetchTreatments } from '../../SessionStep/hooks/useFetchTreatments';

interface ConfirmationItemProps {
  icon: React.ElementType;
  label: string;
  description?: string;
  emphasized?: boolean;
  alignStart?: boolean;
}

const ConfirmationItem = ({
  icon: Icon,
  label,
  description,
  emphasized = false,
  alignStart = false,
}: ConfirmationItemProps): React.ReactElement => (
  <div className={`flex gap-2 ${alignStart ? 'items-start' : 'items-center'}`}>
    <Icon className="size-4 shrink-0 text-teal-600" />
    <div className="flex min-w-0 flex-col">
      <Text
        variant="text-sm-regular"
        className={`text-denim-800 ${emphasized ? 'font-medium' : 'font-normal'}`}
      >
        {label}
      </Text>
      {description ? (
        <Text variant="text-xs-regular" className="text-gray-500">
          {description}
        </Text>
      ) : null}
    </div>
  </div>
);
export const SessionSummary = (): React.ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.APPOINTMENTS);
  const { watch } = useFormContext<AppointmentsReserveFormData>();
  const {
    patientId,
    specialityBooked,
    psychologistServiceBooked,
    city,
    backofficeCenterId,
    dateTime,
  } = watch();

  const { data: specialities } = useFetchSpecialities(patientId);
  const { data: treatments } = useFetchTreatments();
  const centersOptions = useGetSpecialityOptions(AppointmentsReservePayload.backofficeCenterId);

  const specialityLabel =
    mapSpecialities(specialities ?? []).find(
      specialityOption => String(specialityOption.value) === String(specialityBooked)
    )?.label ?? '';

  const treatmentLabel =
    mapTreatments(specialityBooked, treatments ?? []).find(
      treatmentOption => String(treatmentOption.value) === String(psychologistServiceBooked)
    )?.label ?? '';

  const center = centersOptions.find(
    centerOption => String(centerOption.value) === String(backofficeCenterId)
  );

  return (
    <div className="flex flex-col gap-2 p-6">
      <div className="flex flex-col gap-2">
        <ConfirmationItem
          icon={Calendar}
          label={dateTime ? formatUnifiedDateWithMonth(dateTime) : ''}
          emphasized
        />
        <ConfirmationItem
          icon={Clock}
          label={dateTime ? formatTime(dateTime) : ''}
          emphasized={false}
        />
      </div>
      <div className="my-2 border-t border-teal-300" />
      <div className="flex flex-col gap-2">
        <ConfirmationItem icon={ClipboardPen} label={specialityLabel} />
        <ConfirmationItem icon={Stethoscope} label={treatmentLabel} />
        <ConfirmationItem
          icon={Building}
          label={t(`AppointmentsReserve.Steps.confirmation.contactMethod.in-person`)}
        />
        <ConfirmationItem icon={MapPin} label={city} />
        {center ? (
          <ConfirmationItem
            icon={Building}
            label={center.label}
            description={center.description}
            alignStart
          />
        ) : null}
      </div>
    </div>
  );
};
